import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, X } from 'lucide-react';

import type { ForecastReviewAlert } from '@/services/forecastReviewService';
import { getForecastAlertBusinessMessage } from '../utils/forecastAlertDisplay';

export type AlertReactivateModalPhase = 'confirm' | 'applying' | 'done' | 'error';

interface AlertReactivateModalProps {
  open: boolean;
  alert: ForecastReviewAlert | null;
  phase: AlertReactivateModalPhase;
  errorMessage?: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

export function AlertReactivateModal({
  open,
  alert,
  phase,
  errorMessage,
  onConfirm,
  onClose,
}: AlertReactivateModalProps) {
  const { t } = useTranslation();

  if (!open || !alert) return null;

  const busy = phase === 'applying';
  const done = phase === 'done';
  const errored = phase === 'error';

  const content = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-[1px]"
        onClick={busy ? undefined : onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="alert-reactivate-modal-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="alert-reactivate-modal-title" className="text-base font-semibold text-gray-900">
              {done
                ? t('forecastReview.alerts.reactivateModal.doneTitle')
                : t('forecastReview.alerts.reactivateModal.title')}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {alert.element_name ?? alert.element_code ?? `#${alert.id}`}
            </p>
          </div>
          {!busy ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={t('forecastReview.alerts.reactivateModal.close')}
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>

        <div className="space-y-3 px-5 py-4">
          {busy ? (
            <p className="inline-flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('forecastReview.alerts.reactivateModal.applying')}
            </p>
          ) : null}
          {done ? (
            <p className="text-sm text-emerald-800">{t('forecastReview.alerts.reactivateModal.success')}</p>
          ) : null}
          {errored ? (
            <p className="text-sm text-red-700">
              {errorMessage ?? t('forecastReview.alerts.reactivateModal.errorGeneric')}
            </p>
          ) : null}
          {!done && !busy ? (
            <>
              <p className="text-sm text-gray-600">{t('forecastReview.alerts.reactivateModal.message')}</p>
              <p className="text-sm text-gray-500">{getForecastAlertBusinessMessage(alert, t)}</p>
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
          {done || errored ? (
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              onClick={onClose}
            >
              {t('forecastReview.alerts.reactivateModal.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
                onClick={onClose}
                disabled={busy}
              >
                {t('forecastReview.alerts.reactivateModal.cancel')}
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-medium text-sky-900 hover:bg-sky-100 disabled:opacity-50"
                onClick={() => void onConfirm()}
                disabled={busy}
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('forecastReview.alerts.reactivateModal.confirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
